import apiClient from './client';
import {
  User,
  Tenant,
  Question,
  QuestionBank,
  Exam,
  ExamAttempt,
  AttemptResult,
  ApiResponse,
  PaginatedResponse,
  DashboardStats,
  ExamAnalytics,
  StudentAnalytics,
  QuestionAnalytics,
  Violation,
} from '@/types';

// Students Search API (optimized for enrollment)
export const studentsApi = {
  search: (params: { q: string; page?: number; per_page?: number; exclude_exam_id?: string }) =>
    apiClient.get<ApiResponse<{ students: Array<{ id: string; email: string; username: string; name: string }>; page: number; per_page: number; total: number }>>('/students/search', { params }),
};

// Enrollment Links API (Magic Links for campaigns)
export const enrollmentLinksApi = {
  generate: (examId: string, data: { name: string; description?: string; campaign_id?: string; expires_at?: string; max_uses?: number; allowed_email_domains?: string[] }) =>
    apiClient.post<ApiResponse<{ id: string; token: string; link_url: string }>>(`/exams/${examId}/enrollment-links`, data),

  list: (examId: string) =>
    apiClient.get<ApiResponse<any[]>>(`/exams/${examId}/enrollment-links`),

  deactivate: (linkId: string) =>
    apiClient.delete(`/enrollment-links/${linkId}`),

  getStats: (linkId: string) =>
    apiClient.get<ApiResponse<any>>(`/enrollment-links/${linkId}/stats`),

  getPreview: (token: string) =>
    apiClient.get<ApiResponse<any>>(`/enroll/${token}/preview`),

  confirmEnrollment: (data: { token: string; utm_source?: string; utm_medium?: string; utm_campaign?: string; utm_term?: string; utm_content?: string }) =>
    apiClient.post<ApiResponse<any>>('/enroll/confirm', data),
};

// Student Groups API (Cohort-based enrollment)
export const studentGroupsApi = {
  create: (data: { name: string; description?: string; group_type: string; grade_level?: string; subject?: string; tags?: string[]; campaign_id?: string }) =>
    apiClient.post<ApiResponse<any>>('/groups', data),

  list: (activeOnly: boolean = true) =>
    apiClient.get<ApiResponse<any[]>>('/groups', { params: { active_only: activeOnly } }),

  get: (groupId: string) =>
    apiClient.get<ApiResponse<any>>(`/groups/${groupId}`),

  update: (groupId: string, data: { name?: string; description?: string; group_type?: string; grade_level?: string; subject?: string; tags?: string[]; is_active?: boolean }) =>
    apiClient.put<ApiResponse<any>>(`/groups/${groupId}`, data),

  delete: (groupId: string) =>
    apiClient.delete(`/groups/${groupId}`),

  addMembers: (groupId: string, studentIds: string[]) =>
    apiClient.post<ApiResponse<any>>(`/groups/${groupId}/members`, { student_ids: studentIds }),

  removeMember: (groupId: string, studentId: string) =>
    apiClient.delete(`/groups/${groupId}/members/${studentId}`),

  getMembers: (groupId: string) =>
    apiClient.get<ApiResponse<any[]>>(`/groups/${groupId}/members`),

  enrollGroup: (groupId: string, examId: string) =>
    apiClient.post<ApiResponse<any>>(`/groups/${groupId}/enroll`, { exam_id: examId }),

  getStats: () =>
    apiClient.get<ApiResponse<any[]>>('/groups/stats'),
};

// Users API
export const usersApi = {
  list: (params?: { page?: number; per_page?: number; role?: string }) =>
    apiClient.get<ApiResponse<PaginatedResponse<User>>>('/users', { params }),

  get: (id: string) =>
    apiClient.get<ApiResponse<User>>(`/users/${id}`),

  create: (data: Partial<User> & { password: string }) =>
    apiClient.post<ApiResponse<User>>('/users', data),

  update: (id: string, data: Partial<User>) =>
    apiClient.put<ApiResponse<User>>(`/users/${id}`, data),

  delete: (id: string, permanent?: boolean) =>
    apiClient.delete(`/users/${id}`, { params: { permanent } }),

  getUserServices: (id: string) =>
    apiClient.get(`/users/${id}/services`),

  changePassword: (data: { old_password: string; new_password: string }) =>
    apiClient.post('/users/change-password', data),

  // Bulk operations
  bulkImport: (formData: FormData) =>
    apiClient.post<ApiResponse<{ total: number; success: number; failed: number; errors: string[] }>>('/users/bulk-import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  bulkActivate: (userIds: string[]) =>
    apiClient.post<ApiResponse<{ count: number }>>('/users/bulk-activate', { user_ids: userIds }),

  bulkDeactivate: (userIds: string[]) =>
    apiClient.post<ApiResponse<{ count: number }>>('/users/bulk-deactivate', { user_ids: userIds }),

  bulkDelete: (userIds: string[]) =>
    apiClient.post<ApiResponse<{ count: number }>>('/users/bulk-delete', { user_ids: userIds }),

  bulkResetPassword: (userIds: string[]) =>
    apiClient.post<ApiResponse<{ count: number }>>('/users/bulk-reset-password', { user_ids: userIds }),

  bulkSendEmail: (userIds: string[], data: { subject: string; body: string }) =>
    apiClient.post<ApiResponse<{ count: number }>>('/users/bulk-send-email', { user_ids: userIds, ...data }),

  exportAll: () =>
    apiClient.get('/users/export', { responseType: 'blob' }),
};

// Tenants API
export const tenantsApi = {
  list: () =>
    apiClient.get<ApiResponse<Tenant[]>>('/tenants'),

  get: (id: string) =>
    apiClient.get<ApiResponse<Tenant>>(`/tenants/${id}`),

  getByDomain: (domain: string) =>
    apiClient.get<ApiResponse<Tenant>>('/tenants/by-domain', { params: { domain } }),

  create: (data: Partial<Tenant>) =>
    apiClient.post<ApiResponse<Tenant>>('/tenants', data),

  update: (id: string, data: Partial<Tenant>) =>
    apiClient.put<ApiResponse<Tenant>>(`/tenants/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/tenants/${id}`),
};

// Question Banks API
export const questionBanksApi = {
  list: (params?: { page?: number; per_page?: number; subject?: string }) =>
    apiClient.get<ApiResponse<PaginatedResponse<QuestionBank>>>('/question-banks', { params }),

  get: (id: string) =>
    apiClient.get<ApiResponse<QuestionBank>>(`/question-banks/${id}`),

  create: (data: Partial<QuestionBank>) =>
    apiClient.post<ApiResponse<QuestionBank>>('/question-banks', data),

  update: (id: string, data: Partial<QuestionBank>) =>
    apiClient.put<ApiResponse<QuestionBank>>(`/question-banks/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/question-banks/${id}`),

  getStatistics: (id: string) =>
    apiClient.get<ApiResponse<any>>(`/question-banks/${id}/statistics`),

  duplicate: (id: string, name: string) =>
    apiClient.post<ApiResponse<QuestionBank>>(`/question-banks/${id}/duplicate`, { name }),
};

// Questions API
export const questionsApi = {
  list: (params?: { page?: number; per_page?: number; question_bank_id?: string; difficulty?: string; search?: string }) =>
    apiClient.get<ApiResponse<PaginatedResponse<Question>>>('/questions', { params }),

  getAllFromBank: (bankId: string) =>
    apiClient.get<ApiResponse<{ questions: Question[]; total: number }>>(`/questions/bank/${bankId}/all`),

  get: (id: string) =>
    apiClient.get<ApiResponse<Question>>(`/questions/${id}`),

  create: (data: Partial<Question>) =>
    apiClient.post<ApiResponse<Question>>('/questions', data),

  update: (id: string, data: Partial<Question>) =>
    apiClient.put<ApiResponse<Question>>(`/questions/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/questions/${id}`),

  searchByTags: (tags: string[]) =>
    apiClient.get<ApiResponse<Question[]>>('/questions/search/by-tags', { params: { tags: tags.join(',') } }),
};

// Exams API
export const examsApi = {
  list: (params?: { page?: number; per_page?: number; status?: string }) =>
    apiClient.get<ApiResponse<PaginatedResponse<Exam>>>('/exams', { params }),

  statusCounts: () =>
    apiClient.get<ApiResponse<Record<string, number>>>('/exams/status-counts'),

  listActive: () =>
    apiClient.get<ApiResponse<Exam[]>>('/exams/active'),

  listUpcoming: () =>
    apiClient.get<ApiResponse<Exam[]>>('/exams/upcoming'),

  get: (id: string) =>
    apiClient.get<ApiResponse<Exam>>(`/exams/${id}`),

  getQuestions: (id: string) =>
    apiClient.get<ApiResponse<Question[]>>(`/exams/${id}/questions`),

  create: (data: Partial<Exam>) =>
    apiClient.post<ApiResponse<Exam>>('/exams', data),

  update: (id: string, data: Partial<Exam>) =>
    apiClient.put<ApiResponse<Exam>>(`/exams/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/exams/${id}`),

  publish: (id: string) =>
    apiClient.post<ApiResponse<Exam>>(`/exams/${id}/publish`),

  activate: (id: string) =>
    apiClient.post<ApiResponse<Exam>>(`/exams/${id}/activate`),

  cancel: (id: string) =>
    apiClient.post<ApiResponse<Exam>>(`/exams/${id}/cancel`),

  // Enrollment endpoints
  enrollStudents: (id: string, studentIds: string[]) =>
    apiClient.post<ApiResponse<{ enrolled_count: number; enrolled_ids: string[] }>>(`/exams/${id}/enroll`, { student_ids: studentIds }),

  unenrollStudent: (id: string, studentId: string) =>
    apiClient.delete(`/exams/${id}/enroll/${studentId}`),

  getEnrolledStudents: (id: string) =>
    apiClient.get<ApiResponse<any[]>>(`/exams/${id}/enrollments`),
};

// Enhanced attempt-detail shapes (returned by /teacher/attempts/:id/full-detail).
// Keep in sync with internal/usecase/attempt_detail_usecase.go.
export interface AttemptFullDetailOption {
  id: string;
  text: string;
  is_correct: boolean;
  is_picked: boolean;
}
export interface AttemptFullDetailAnswer {
  question_id: string;
  question_type: string;
  question_text: string;
  points: number;
  options: AttemptFullDetailOption[];
  selected_raw: string[] | null;
  selected_labels: string[] | null;
  correct_labels: string[] | null;
  text_answer?: string;
  is_correct?: boolean;
  score?: number;
  time_taken_seconds: number;
  anomalies: string[] | null;
  explanation?: string;
}
export interface AttemptFullDetailViolation {
  source: string;
  type: string;
  severity: string;
  timestamp: string;
  description?: string;
  screenshot_url?: string;
  video_url?: string;
  ai_confidence?: number;
}
export interface AttemptFullDetailSnapshot {
  url: string;
  timestamp: string;
  kind: 'face' | 'screenshot' | 'violation_snapshot' | 'webcam_recording' | 'screen_recording';
  note?: string;
}
export interface AttemptFullDetailTimingStats {
  answered_count: number;
  total_questions: number;
  total_time_seconds: number;
  avg_seconds_per_answer: number;
  median_seconds: number;
  min_seconds: number;
  max_seconds: number;
  fast_answer_count: number;
  very_fast_threshold_s: number;
  histogram: number[];
  histogram_edges: number[];
}
export interface AttemptFullDetail {
  attempt: {
    id: string;
    status: string;
    start_time: string;
    end_time?: string;
    duration_seconds: number;
    score?: number;
    percentage?: number;
    result?: string;
    violation_count: number;
    session_token: string;
  };
  exam: {
    id: string;
    title: string;
    max_attempts: number;
    passing_marks: number;
    total_marks: number;
    duration_minutes: number;
    shuffle_questions: boolean;
    is_proctored: boolean;
  };
  student: {
    id: string;
    email: string;
    username: string;
    name: string;
  };
  answers: AttemptFullDetailAnswer[];
  timing_stats: AttemptFullDetailTimingStats;
  violations: AttemptFullDetailViolation[];
  snapshots: AttemptFullDetailSnapshot[];
  patterns: {
    ip_address?: string;
    user_agent?: string;
    device_fingerprint?: Record<string, unknown>;
    shuffled_order: boolean;
  };
}

// Cheating-detection scan shapes. Keep in sync with internal/usecase/cheating_detection_usecase.go.
export interface SuspiciousMember {
  attempt_id: string;
  student_id: string;
  student_name: string;
  student_email: string;
  ip_address?: string;
  score?: number;
  percentage?: number;
  result?: string;
  start_time: string;
  end_time?: string;
}
export interface SuspiciousSameWrongItem {
  question_id: string;
  question_text: string;
  picked_raw: string;
  picked_label: string;
}
export interface SuspiciousCluster {
  id: string;
  match_percentage: number;
  identical_questions: number;
  compared_questions: number;
  shared_ips?: string[];
  time_window_seconds: number;
  signals: string[];
  same_wrong_answers?: SuspiciousSameWrongItem[];
  members: SuspiciousMember[];
}
export interface SuspiciousIPPair {
  ip_address: string;
  members: SuspiciousMember[];
}
export interface SuspiciousReport {
  exam_id: string;
  exam_title: string;
  scanned_attempts: number;
  eligible_attempts: number;
  threshold: number;
  clusters: SuspiciousCluster[];
  ip_only_pairs: SuspiciousIPPair[];
  generated_at: string;
}

// Attempts API
export const attemptsApi = {
  start: (examId: string) =>
    apiClient.post<ApiResponse<ExamAttempt>>(`/attempts/exams/${examId}/start`),

  completeSystemCheck: (attemptId: string) =>
    apiClient.post<ApiResponse<ExamAttempt>>(`/attempts/${attemptId}/complete-system-check`),

  // Save a single answer mid-exam. Idempotent — the backend upserts on
  // (attempt_id, question_id). Call this on every answer change (debounced)
  // so students don't lose progress if the browser crashes or the exam is
  // force-terminated by proctoring violations.
  saveAnswer: (
    attemptId: string,
    data: {
      question_id: string;
      selected_answers: string[];
      text_answer: string;
      time_spent_seconds: number;
    }
  ) =>
    apiClient.post<ApiResponse<{ attempt_id: string; question_id: string }>>(
      `/attempts/${attemptId}/answers`,
      data
    ),

  // Fetch all previously-saved answers for an in-progress attempt.
  // Used to hydrate frontend state on exam page reload.
  getSavedAnswers: (attemptId: string) =>
    apiClient.get<
      ApiResponse<{
        answers: Array<{
          question_id: string;
          selected_answers: string[];
          text_answer: string;
          time_spent_seconds: number;
        }>;
        count: number;
      }>
    >(`/attempts/${attemptId}/answers`),

  submit: (id: string, answers: any[]) =>
    apiClient.post<ApiResponse<ExamAttempt>>(`/attempts/${id}/submit`, { answers }),

  get: (id: string) =>
    apiClient.get<ApiResponse<ExamAttempt>>(`/attempts/${id}`),

  getResult: (id: string) =>
    apiClient.get<ApiResponse<AttemptResult>>(`/attempts/${id}/result`),

  listMy: (params?: { page?: number; per_page?: number }) =>
    apiClient.get<ApiResponse<PaginatedResponse<ExamAttempt>>>('/attempts/my-attempts', { params }),

  listByExam: (examId: string, params?: { page?: number; per_page?: number }) =>
    apiClient.get<ApiResponse<PaginatedResponse<ExamAttempt>>>('/attempts/my-attempts', {
      params: { ...params, exam_id: examId }
    }),

  // List all attempts for an exam (teacher/admin only)
  listAllByExam: (examId: string, params?: { limit?: number; offset?: number }) =>
    apiClient.get<ApiResponse<{ data: ExamAttempt[]; total: number; limit: number; offset: number }>>(`/attempts/exams/${examId}/attempts`, { params }),

  grade: (id: string, data: { score: number; feedback?: string }) =>
    apiClient.post<ApiResponse<ExamAttempt>>(`/attempts/${id}/grade`, data),

  listPendingGrading: (params?: { page?: number; per_page?: number }) =>
    apiClient.get<ApiResponse<PaginatedResponse<ExamAttempt>>>('/attempts/pending-grading', { params }),

  delete: (id: string) =>
    apiClient.delete(`/attempts/${id}`),

  recordViolation: (id: string, data: Partial<Violation>) =>
    apiClient.post<ApiResponse<Violation>>(`/attempts/${id}/violations`, data),

  terminate: (id: string, data: { reason: string; violations?: any[] }) =>
    apiClient.post<ApiResponse<ExamAttempt>>(`/attempts/${id}/terminate`, data),

  // Teacher/admin: cancel a specific attempt so it no longer counts toward the student's quota.
  // Allowed for the exam's creator (teacher/instructor) or any admin role.
  adminCancel: (attemptId: string, reason: string) =>
    apiClient.post<ApiResponse<ExamAttempt>>(`/teacher/attempts/${attemptId}/cancel`, { reason }),

  // Teacher/admin: enhanced attempt detail — per-question breakdown, proctoring evidence,
  // timing stats, and pattern data for post-submission review.
  getFullDetail: (attemptId: string) =>
    apiClient.get<ApiResponse<AttemptFullDetail>>(`/teacher/attempts/${attemptId}/full-detail`),

  // Teacher/admin: scan exam for suspected cheating clusters (similarity + shared IP).
  scanSuspicious: (examId: string) =>
    apiClient.get<ApiResponse<SuspiciousReport>>(`/teacher/exams/${examId}/suspicious-attempts`),

  // Teacher/admin: cancel multiple attempts at once (e.g. all members of a cheating cluster).
  bulkCancel: (attemptIds: string[], reason: string) =>
    apiClient.post<ApiResponse<{
      cancelled: number;
      total: number;
      results: { attempt_id: string; cancelled: boolean; error?: string }[];
    }>>(`/teacher/attempts/bulk-cancel`, { attempt_ids: attemptIds, reason }),

  // Teacher/admin: grant N extra attempts to a specific student for a specific exam.
  // Extra attempts stack on top of exam.max_attempts.
  grantExtraAttempts: (examId: string, studentId: string, extraAttempts: number, reason: string) =>
    apiClient.post<ApiResponse<{ extra_attempts: number; exam_id: string; student_id: string }>>(
      `/teacher/exams/${examId}/students/${studentId}/grant-attempts`,
      { extra_attempts: extraAttempts, reason }
    ),
};

// Proctoring Violation Types
export type ViolationSeverity = 'low' | 'medium' | 'high' | 'critical';
export type RiskLevel = 'low' | 'medium' | 'high' | 'critical';

export interface ViolationRequest {
  violation_type: string;
  severity: ViolationSeverity;
  description?: string;
  screenshot_url?: string;
  video_url?: string;
  ai_confidence?: number;
  metadata?: Record<string, unknown>;
}

export interface ViolationResponse {
  id: string;
  attempt_id: string;
  violation_type: string;
  severity: ViolationSeverity;
  description: string;
  screenshot_url?: string;
  video_url?: string;
  ai_confidence?: number;
  is_reviewed: boolean;
  is_false_positive: boolean;
  timestamp: string;
  created_at: string;
  student_id?: string;
  penalty_percent?: number;
  penalty_points?: number;
  penalty_applied?: boolean;
  penalty_notes?: string;
}

export interface ViolationStats {
  total_violations: number;
  by_severity: Record<ViolationSeverity, number>;
  by_type: Record<string, number>;
  reviewed_count: number;
  false_positive_count: number;
  unreviewed_count: number;
  risk_level: RiskLevel;
}

export interface StudentViolationSummary {
  student_id: string;
  student_name: string;
  email: string;
  total_violations: number;
  critical_count: number;
  high_count: number;
  medium_count: number;
  low_count: number;
  unreviewed_count: number;
  total_penalty: number;
  latest_violation: string;
  attempt_count: number;
}

export interface StudentSummaryListResponse {
  students: StudentViolationSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface RiskScoreResponse {
  id: string;
  session_id: string;
  attempt_id: string;
  risk_level: RiskLevel;
  total_score: number;
  factors: Array<{ factor: string; score: number; count: number }>;
  calculated_at: string;
}

export interface SessionRequest {
  attempt_id: string;
  webcam_enabled: boolean;
  screen_share_enabled: boolean;
  audio_enabled: boolean;
  ai_enabled: boolean;
}

export interface SessionResponse {
  id: string;
  attempt_id: string;
  webcam_enabled: boolean;
  screen_share_enabled: boolean;
  audio_enabled: boolean;
  ai_enabled: boolean;
  session_started_at: string;
  session_ended_at?: string;
  recording_url?: string;
}

export interface EvidenceRequest {
  violation_id: string;
  evidence_type: 'screenshot' | 'webcam_frame' | 'audio_clip' | 'video_clip' | 'screen_record' | 'log';
  file_data: string; // base64 encoded
  mime_type?: string;
  duration?: number;
  metadata?: Record<string, unknown>;
}

export interface ProctoringFileInfo {
  url: string;
  path: string;
  size: number;
  content_type?: string;
}

// Proctoring API
export const proctoringApi = {
  // Legacy endpoints (backward compatibility)
  getViolations: (attemptId: string) =>
    apiClient.get<ApiResponse<Violation[]>>(`/proctoring/attempts/${attemptId}/violations`),

  logTabSwitch: (attemptId: string) =>
    apiClient.post(`/proctoring/attempts/${attemptId}/tab-switch`),

  logCopyPaste: (attemptId: string, data: { content: string }) =>
    apiClient.post(`/proctoring/attempts/${attemptId}/copy-paste`, data),

  recordScreenshot: (attemptId: string, data: { image_data: string }) =>
    apiClient.post(`/proctoring/attempts/${attemptId}/screenshot`, data),

  getExamSettings: (examId: string) =>
    apiClient.get<ApiResponse<any>>(`/proctoring/exams/${examId}/settings`),

  // ===========================================
  // New Phase 6 Proctoring API Endpoints
  // ===========================================

  // Violation Management
  logViolation: (data: ViolationRequest & { attempt_id: string }) =>
    apiClient.post<ApiResponse<ViolationResponse>>('/proctoring/violations', data),

  logViolationBatch: (data: { violations: Array<ViolationRequest & { attempt_id: string }> }) =>
    apiClient.post<ApiResponse<{ ids: string[]; count: number }>>('/proctoring/violations/batch', data),

  getViolation: (violationId: string) =>
    apiClient.get<ApiResponse<ViolationResponse>>(`/proctoring/violations/${violationId}`),

  listViolations: (params?: {
    attempt_id?: string;
    exam_id?: string;
    student_id?: string;
    severity?: ViolationSeverity[];
    violation_types?: string[];
    is_reviewed?: boolean;
    limit?: number;
    offset?: number;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }) =>
    apiClient.get<ApiResponse<{ violations: ViolationResponse[]; total: number; limit: number; offset: number }>>('/proctoring/violations', { params }),

  getViolationStats: (attemptId: string) =>
    apiClient.get<ApiResponse<ViolationStats>>(`/proctoring/attempts/${attemptId}/stats`),

  // Aggregated student summary for one exam — one row per student with totals.
  // Backs the teacher violation review page student cards (search + pagination).
  getExamStudentSummary: (examId: string, params?: {
    search?: string;
    limit?: number;
    offset?: number;
  }) =>
    apiClient.get<ApiResponse<StudentSummaryListResponse>>(
      `/proctoring/exams/${examId}/student-summary`,
      { params },
    ),

  // Violation Review
  reviewViolation: (violationId: string, data: {
    notes?: string;
    is_false_positive: boolean;
  }) =>
    apiClient.post<ApiResponse<ViolationResponse>>(`/proctoring/violations/${violationId}/review`, data),

  bulkReviewViolations: (data: {
    violation_ids: string[];
    notes?: string;
    is_false_positive: boolean;
  }) =>
    apiClient.post<ApiResponse<{ updated_count: number }>>('/proctoring/violations/bulk-review', data),

  adjustViolationPenalty: (data: { violation_id: string; penalty_points: number; notes: string }) =>
    apiClient.post<ApiResponse<ViolationResponse>>(`/proctoring/violations/${data.violation_id}/adjust-penalty`, {
      penalty_points: data.penalty_points,
      notes: data.notes,
    }),

  // Session Management
  createSession: (data: SessionRequest) =>
    apiClient.post<ApiResponse<SessionResponse>>('/proctoring/sessions', data),

  getSession: (sessionId: string) =>
    apiClient.get<ApiResponse<SessionResponse>>(`/proctoring/sessions/${sessionId}`),

  getSessionByAttempt: (attemptId: string) =>
    apiClient.get<ApiResponse<SessionResponse>>(`/proctoring/sessions/attempt/${attemptId}`),

  endSession: (sessionId: string) =>
    apiClient.post<ApiResponse<SessionResponse>>(`/proctoring/sessions/${sessionId}/end`),

  getActiveSessions: (params?: { limit?: number; offset?: number }) =>
    apiClient.get<ApiResponse<{ sessions: SessionResponse[]; total: number }>>('/proctoring/sessions/active', { params }),

  // Evidence Management
  uploadEvidence: (data: EvidenceRequest) =>
    apiClient.post<ApiResponse<{ id: string; file_url: string; thumbnail_url?: string }>>('/proctoring/evidence', data),

  getEvidenceByViolation: (violationId: string) =>
    apiClient.get<ApiResponse<Array<{
      id: string;
      evidence_type: string;
      file_url: string;
      thumbnail_url?: string;
      file_size: number;
      mime_type: string;
      duration?: number;
    }>>>(`/proctoring/violations/${violationId}/evidence`),

  // Risk Score Management
  calculateRiskScore: (attemptId: string) =>
    apiClient.post<ApiResponse<RiskScoreResponse>>(`/proctoring/attempts/${attemptId}/risk-score`),

  getRiskScore: (attemptId: string) =>
    apiClient.get<ApiResponse<RiskScoreResponse>>(`/proctoring/attempts/${attemptId}/risk-score`),

  getHighRiskAttempts: (examId: string, params?: { min_score?: number; limit?: number }) =>
    apiClient.get<ApiResponse<Array<{
      attempt_id: string;
      student_id: string;
      risk_level: RiskLevel;
      total_score: number;
      violation_count: number;
    }>>>(`/proctoring/exams/${examId}/high-risk`, { params }),

  getRecentViolations: (examId: string, params?: { since?: string; limit?: number }) =>
    apiClient.get<ApiResponse<ViolationResponse[]>>(`/proctoring/exams/${examId}/recent-violations`, { params }),

  // Face snapshot upload for pre-exam verification
  uploadFaceSnapshot: (data: { attempt_id: string; exam_id: string; image_base64: string; exam_title?: string }) =>
    apiClient.post<ApiResponse<{ url: string; path: string; uploaded_at: string }>>(`/proctoring/face-snapshot?exam_id=${data.exam_id}&exam_title=${encodeURIComponent(data.exam_title || '')}`, {
      attempt_id: data.attempt_id,
      image_base64: data.image_base64,
    }),

  // Violation screenshot upload
  uploadViolationScreenshot: (data: { attempt_id: string; exam_id: string; violation_type: string; image_base64: string }) =>
    apiClient.post<ApiResponse<{ url: string; path: string; uploaded_at: string }>>(`/proctoring/violation-screenshot?exam_id=${data.exam_id}`, {
      attempt_id: data.attempt_id,
      violation_type: data.violation_type,
      image_base64: data.image_base64,
    }),

  // Periodic webcam snapshot — fired on a timer (every ~45s) during the
  // exam regardless of violations. Gives instructors a continuous record
  // of the student's presence throughout the session.
  uploadPeriodicScreenshot: (data: { attempt_id: string; exam_id: string; image_base64: string; exam_title?: string }) =>
    apiClient.post<ApiResponse<{ url: string; path: string; uploaded_at: string }>>(
      `/proctoring/periodic-screenshot?exam_id=${data.exam_id}${data.exam_title ? `&exam_title=${encodeURIComponent(data.exam_title)}` : ''}`,
      {
        attempt_id: data.attempt_id,
        image_base64: data.image_base64,
      }
    ),

  // Missing endpoints
  getUnreviewedViolations: (params?: { limit?: number; offset?: number }) =>
    apiClient.get<ApiResponse<{ violations: ViolationResponse[]; total: number }>>('/proctoring/violations/unreviewed', { params }),

  getSessionSummary: (sessionId: string) =>
    apiClient.get<ApiResponse<SessionResponse>>(`/proctoring/sessions/${sessionId}/summary`),

  sendHeartbeat: (data: { attempt_id: string; session_id: string }) =>
    apiClient.post<ApiResponse<{ status: string }>>('/proctoring/heartbeat', data),

  getConfig: () =>
    apiClient.get<ApiResponse<any>>('/proctoring/config'),

  startProctoring: (data: { attempt_id: string; exam_id: string }) =>
    apiClient.post<ApiResponse<SessionResponse>>('/proctoring/sessions/start', data),

  endProctoring: (data: { attempt_id: string; session_id: string }) =>
    apiClient.post<ApiResponse<SessionResponse>>('/proctoring/sessions/end', data),

  uploadRecordingChunk: (data: FormData) =>
    apiClient.post<ApiResponse<{ chunk_id: string }>>('/proctoring/recording/chunk', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  listAttemptFiles: (attemptId: string, examId: string) =>
    apiClient.get<ApiResponse<ProctoringFileInfo[]>>(`/proctoring/attempts/${attemptId}/files`, { params: { exam_id: examId } }),

  // AI Vision Proctoring (Claude Vision API)
  getAIVisionStatus: () =>
    apiClient.get<ApiResponse<{ enabled: boolean; provider: string; model: string }>>('/proctoring/ai-vision/status'),

  analyzeAIVisionFrame: (data: { attempt_id: string; exam_id: string; image_data: string; frame_index: number }) =>
    apiClient.post<ApiResponse<{
      person_count: number;
      face_detected: boolean;
      face_count: number;
      looking_at_screen: boolean;
      prohibited_items: string[];
      risk_level: string;
      description: string;
      violations: string[];
      confidence: number;
      processing_time_ms: number;
    }>>('/proctoring/ai-vision/analyze', data),
};

// Proctoring WebSocket Management API
export const proctoringWsApi = {
  // Statistics
  getStats: () =>
    apiClient.get<ApiResponse<{ total_connections: number }>>('/proctoring-ws/stats'),

  getExamStats: (examId: string) =>
    apiClient.get<ApiResponse<{
      exam_id: string;
      active_attempts: number;
      connected_students: number;
    }>>(`/proctoring-ws/exams/${examId}/stats`),

  // Proctor Subscriptions
  subscribeToExam: (examId: string, clientId: string) =>
    apiClient.post(`/proctoring-ws/exams/${examId}/subscribe`, { client_id: clientId }),

  unsubscribeFromExam: (examId: string, clientId: string) =>
    apiClient.post(`/proctoring-ws/exams/${examId}/unsubscribe`, { client_id: clientId }),

  // Student Connection Management
  checkStudentConnection: (attemptId: string) =>
    apiClient.get<ApiResponse<{ attempt_id: string; connected: boolean }>>(`/proctoring-ws/attempts/${attemptId}/connection`),

  // Proctor Actions
  sendWarning: (attemptId: string, data: {
    warning_type: string;
    message: string;
    remaining: number;
  }) =>
    apiClient.post(`/proctoring-ws/attempts/${attemptId}/warning`, data),

  terminateExam: (attemptId: string, data: {
    exam_id: string;
    reason: string;
  }) =>
    apiClient.post(`/proctoring-ws/attempts/${attemptId}/terminate`, data),
};

// Analytics API
export const analyticsApi = {
  getDashboard: (params?: { days?: number }) =>
    apiClient.get<ApiResponse<DashboardStats>>('/analytics/dashboard', { params }),

  getTenantAnalytics: () =>
    apiClient.get<ApiResponse<any>>('/analytics/tenant'),

  getTenantTimeSeries: (params: { start_date: string; end_date: string }) =>
    apiClient.get<ApiResponse<any>>('/analytics/tenant/time-series', { params }),

  getExamAnalytics: (examId: string) =>
    apiClient.get<ApiResponse<ExamAnalytics>>(`/analytics/exams/${examId}`),

  getExamsByTenant: (params?: { days?: number }) =>
    apiClient.get<ApiResponse<{ exams: ExamAnalytics[] | null; total: number; limit: number; offset: number }>>('/analytics/exams', { params }),

  getExamTimeSeries: (examId: string, params: { start_date: string; end_date: string }) =>
    apiClient.get<ApiResponse<any>>(`/analytics/exams/${examId}/time-series`, { params }),

  getTopStudents: (params?: { limit?: number; days?: number }) =>
    apiClient.get<ApiResponse<any>>('/analytics/students/top', { params }),

  getStudentAnalytics: (studentId: string) =>
    apiClient.get<ApiResponse<StudentAnalytics>>(`/analytics/students/${studentId}`),

  getStudentPerformanceByExam: (studentId: string, examId: string) =>
    apiClient.get<ApiResponse<any>>(`/analytics/students/${studentId}/exams/${examId}`),

  getDifficultQuestions: (params?: { limit?: number; threshold?: number; days?: number }) =>
    apiClient.get<ApiResponse<QuestionAnalytics[]>>('/analytics/questions/difficult', { params }),

  getQuestionAnalytics: (questionId: string) =>
    apiClient.get<ApiResponse<QuestionAnalytics>>(`/analytics/questions/${questionId}`),

  getQuestionAnalyticsByBank: (bankId: string) =>
    apiClient.get<ApiResponse<QuestionAnalytics[]>>(`/analytics/question-banks/${bankId}/questions`),

  getViolationAnalytics: (params?: { days?: number }) =>
    apiClient.get<ApiResponse<any>>('/analytics/violations', { params }),

  getViolationsByExam: (examId: string) =>
    apiClient.get<ApiResponse<any>>(`/analytics/violations/exams/${examId}`),

  // =========================================================================
  // Phase 6.2: Dashboard Analytics Endpoints
  // =========================================================================

  // Enhanced Dashboard - comprehensive with all Phase 6.2 features
  getEnhancedDashboard: () =>
    apiClient.get<ApiResponse<EnhancedDashboardData>>('/analytics/dashboard/enhanced'),

  // Proctor Dashboard - real-time monitoring stats
  getProctorDashboard: () =>
    apiClient.get<ApiResponse<ProctorDashboardStats>>('/analytics/dashboard/proctor'),

  // Live Exam Stats - real-time stats for active exams
  getLiveExamStats: () =>
    apiClient.get<ApiResponse<LiveExamStats[]>>('/analytics/live-exams'),

  // Violation Heatmap - patterns by time
  getViolationHeatmap: (params?: { days?: number }) =>
    apiClient.get<ApiResponse<ViolationHeatmapData[]>>('/analytics/violations/heatmap', { params }),

  // Recent Violations - latest violations list
  getRecentViolations: (params?: { limit?: number }) =>
    apiClient.get<ApiResponse<RecentViolation[]>>('/analytics/violations/recent', { params }),

  // Exam Comparison - comparative analytics for multiple exams
  getExamComparison: (examIds: string[]) =>
    apiClient.get<ApiResponse<ExamComparisonData[]>>('/analytics/exams/compare', {
      params: { exam_ids: examIds.join(',') },
    }),

  // Trend Analysis - metric trends over time
  getTrendAnalysis: (params?: { metric?: string; period?: string }) =>
    apiClient.get<ApiResponse<TrendAnalysisData[]>>('/analytics/trends', { params }),

  // Performance Trends - daily performance data
  getPerformanceTrends: (params?: { days?: number }) =>
    apiClient.get<ApiResponse<PerformanceTrendData[]>>('/analytics/performance/trends', { params }),

  // Student Risk Profiles - high-risk students
  getStudentRiskProfiles: (params?: { min_risk_score?: number; limit?: number }) =>
    apiClient.get<ApiResponse<StudentRiskProfile[]>>('/analytics/students/risk-profiles', { params }),

  // Campaign Enrollment Trends - enrollment trends over time by source
  getCampaignTrends: (params?: { days?: number }) =>
    apiClient.get('/analytics/campaigns/trends', { params }),
};

// =========================================================================
// Phase 6.2: Dashboard Analytics Types
// =========================================================================

export interface ViolationHeatmapData {
  hour: number;
  day_of_week: number;
  violation_type: string;
  count: number;
  severity: number;
}

export interface LiveExamStats {
  exam_id: string;
  exam_title: string;
  active_students: number;
  completed_students: number;
  total_enrolled: number;
  recent_violations: number;
  high_risk_count: number;
  average_risk_score: number;
  average_progress: number;
  time_remaining: number;
  start_time: string;
  end_time: string;
}

export interface ExamComparisonData {
  exam_id: string;
  exam_title: string;
  average_score: number;
  pass_rate: number;
  completion_rate: number;
  violation_rate: number;
  average_time_taken: number;
  total_attempts: number;
}

export interface TrendAnalysisData {
  period: string;
  current_value: number;
  previous_value: number;
  percentage_change: number;
  trend: 'up' | 'down' | 'stable';
  metric_name: string;
}

export interface ProctorDashboardStats {
  total_active_exams: number;
  total_active_students: number;
  total_violations_today: number;
  high_risk_students: number;
  critical_alerts: number;
  violations_by_type: Record<string, number>;
  violations_by_severity: Record<string, number>;
  recent_violations: RecentViolation[];
  active_exam_stats: LiveExamStats[];
}

export interface RecentViolation {
  id: string;
  attempt_id: string;
  student_name: string;
  exam_title: string;
  violation_type: string;
  severity: string;
  description: string;
  created_at: string;
  risk_score: number;
}

export interface StudentRiskProfile {
  student_id: string;
  student_name: string;
  total_violations: number;
  high_severity_count: number;
  average_risk_score: number;
  violation_history: ViolationSummary[];
  risk_level: 'low' | 'medium' | 'high' | 'critical';
}

export interface ViolationSummary {
  violation_type: string;
  count: number;
  last_occurred: string;
}

export interface PerformanceTrendData {
  date: string;
  average_score: number;
  pass_rate: number;
  attempts: number;
  violations: number;
}

export interface EnhancedDashboardData {
  overview: any;
  top_students: any[];
  recent_exams: any[];
  difficult_questions: any[];
  violations: any[];
  time_series: any[];
  proctor_stats: ProctorDashboardStats;
  violation_heatmap: ViolationHeatmapData[];
  metric_trends: Record<string, TrendAnalysisData[]>;
  performance_trends: PerformanceTrendData[];
  high_risk_students: StudentRiskProfile[];
  live_exams: LiveExamStats[];
}

// Uploads API
export const uploadsApi = {
  uploadImage: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<ApiResponse<any>>('/uploads/images', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  uploadDocument: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<ApiResponse<any>>('/uploads/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  downloadFile: (path: string) =>
    apiClient.get(`/uploads/files/${path}`, { responseType: 'blob' }),

  deleteFile: (path: string) =>
    apiClient.delete(`/uploads/files/${path}`),

  listFiles: (params?: { folder?: string }) =>
    apiClient.get<ApiResponse<any>>('/uploads/list', { params }),

  getFileURL: (path: string) =>
    apiClient.get<ApiResponse<{ url: string }>>(`/uploads/url/${path}`),
};

// Question Bank Sharing API
export const questionBankSharingApi = {
  getSharedUsers: (questionBankId: string) =>
    apiClient.get<ApiResponse<any>>(`/question-banks/${questionBankId}/shared-users`),

  shareWithUser: (questionBankId: string, data: { email: string; settings: any }) =>
    apiClient.post<ApiResponse<any>>(`/question-banks/${questionBankId}/share`, data),

  revokeAccess: (questionBankId: string, userId: string) =>
    apiClient.delete(`/question-banks/${questionBankId}/shared-users/${userId}`),

  getShareLinks: (questionBankId: string) =>
    apiClient.get<ApiResponse<any>>(`/question-banks/${questionBankId}/share-links`),

  createShareLink: (questionBankId: string, settings: any) =>
    apiClient.post<ApiResponse<any>>(`/question-banks/${questionBankId}/share-links`, settings),

  deleteShareLink: (linkId: string) =>
    apiClient.delete(`/share-links/${linkId}`),

  resolveShareLink: (token: string) =>
    apiClient.get<ApiResponse<{ bank_id: string; bank_name: string; subject: string; permission: string; owner_email: string }>>(`/share-links/resolve/${token}`),
};

// Tests API
export const testsApi = {
  getDashboard: () =>
    apiClient.get<ApiResponse<any>>('/tests/dashboard'),

  getRuns: (limit: number = 20) =>
    apiClient.get<ApiResponse<any>>(`/tests/runs?limit=${limit}`),

  getRunDetails: (runId: string) =>
    apiClient.get<ApiResponse<any>>(`/tests/runs/${runId}`),
};

// =========================================================================
// Phase 10: Role-Based Dashboard APIs
// =========================================================================

// Student Dashboard Types
export interface StudentProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  avatar_url: string;
  created_at: string;
}

export interface StudentSummary {
  total_exams_taken: number;
  total_exams_passed: number;
  total_exams_failed: number;
  average_score: number;
  highest_score: number;
  lowest_score: number;
  total_time_spent_minutes: number;
  total_violations: number;
  pass_rate: number;
  current_streak: number;
}

export interface StudentExamResult {
  attempt_id: string;
  exam_id: string;
  exam_title: string;
  subject: string;
  score: number;
  total_marks: number;
  max_score?: number; // alias for total_marks
  passing_marks?: number;
  percentage: number;
  grade?: string;
  passed: boolean;
  time_taken: number; // in seconds (from backend)
  time_spent_minutes?: number; // alias (computed)
  total_time?: number;
  rank?: number;
  total_students?: number;
  completed_at?: string;
  violation_count?: number;
  violations_count?: number; // alias
  submitted_at?: string;
  graded_at?: string;
}

export interface UpcomingExam {
  exam_id: string;
  exam_title: string;
  title?: string; // alias for exam_title
  subject: string;
  description?: string;
  start_time: string;
  end_time: string;
  duration: number; // in minutes (from backend)
  duration_minutes?: number; // alias
  total_marks: number;
  passing_marks?: number;
  total_questions?: number;
  is_enrolled?: boolean;
  has_attempted?: boolean;
  is_proctored?: boolean;
  enrolled_at?: string;
}

export interface StudentPerformanceDay {
  date: string;
  exams_taken: number;
  average_score: number;
  passed: number;
  failed: number;
}

export interface SubjectPerformance {
  subject: string;
  exams_taken: number;
  average_score: number;
  highest_score: number;
  pass_rate: number;
}

export interface StudentAchievement {
  id: string;
  title: string;
  description: string;
  icon: string;
  earned_at: string;
  category: string;
}

export interface StudentNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
  action_url: string;
}

export interface StudentDashboard {
  profile: StudentProfile;
  summary: StudentSummary;
  recent_exams: StudentExamResult[];
  upcoming_exams: UpcomingExam[];
  performance_trend: StudentPerformanceDay[];
  subject_performance: SubjectPerformance[];
  achievements: StudentAchievement[];
  notifications: StudentNotification[];
}

// Admin Dashboard Types
export interface AdminUserStats {
  total_users: number;
  total_students: number;
  total_teachers: number;
  total_admins: number;
  active_users: number;
  inactive_users: number;
  new_users_today: number;
  new_users_this_week: number;
  new_users_this_month: number;
}

export interface AdminExamStats {
  total_exams: number;
  active_exams: number;
  scheduled_exams: number;
  completed_exams: number;
  draft_exams: number;
  total_attempts: number;
  average_pass_rate: number;
  average_score: number;
  exams_today: number;
  exams_this_week: number;
}

export interface AdminActivity {
  id: string;
  type: string;
  user_id: string;
  user_name: string;
  description: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface AdminDashboard {
  user_stats: AdminUserStats;
  exam_stats: AdminExamStats;
  recent_activity: AdminActivity[];
}

// Teacher Dashboard Types
export interface TeacherExamSummary {
  exam_id: string;
  title: string;
  subject: string;
  status: string;
  total_enrolled: number;
  total_attempts: number;
  completed_attempts: number;
  average_score: number;
  pass_rate: number;
  start_time: string;
  end_time: string;
}

export interface ScheduledExam {
  exam_id: string;
  title: string;
  subject: string;
  start_time: string;
  end_time: string;
  enrolled_count: number;
  duration_minutes: number;
}

export interface PendingGradingItem {
  attempt_id: string;
  exam_id: string;
  exam_title: string;
  student_id: string;
  student_name: string;
  submitted_at: string;
  questions_to_grade: number;
  auto_score: number;
  max_score: number;
}

export interface ExamResultSummary {
  exam_id: string;
  exam_title: string;
  total_attempts: number;
  average_score: number;
  highest_score: number;
  lowest_score: number;
  pass_rate: number;
  completed_at: string;
}

export interface QuestionBankSummary {
  id: string;
  name: string;
  subject: string;
  total_questions: number;
  questions_by_difficulty: {
    easy: number;
    medium: number;
    hard: number;
  };
  last_updated: string;
}

export interface StudentAlert {
  student_id: string;
  student_name: string;
  alert_type: string;
  message: string;
  exam_id: string;
  exam_title: string;
  created_at: string;
}

export interface TeacherDashboard {
  exams: TeacherExamSummary[];
  today_schedule: ScheduledExam[];
  pending_grading: PendingGradingItem[];
  recent_results: ExamResultSummary[];
  question_bank_stats: QuestionBankSummary[];
  student_alerts: StudentAlert[];
}

// Dashboard API
export const dashboardApi = {
  // =========================================================================
  // Student Dashboard Endpoints
  // =========================================================================
  getStudentDashboard: () =>
    apiClient.get<ApiResponse<StudentDashboard>>('/analytics/dashboard/student'),

  getStudentSummary: () =>
    apiClient.get<ApiResponse<StudentSummary>>('/analytics/student/summary'),

  getStudentRecentExams: (limit?: number) =>
    apiClient.get<ApiResponse<StudentExamResult[]>>('/analytics/student/recent-exams', {
      params: { limit },
    }),

  getStudentUpcomingExams: () =>
    apiClient.get<ApiResponse<UpcomingExam[]>>('/analytics/student/upcoming-exams'),

  getStudentPerformanceTrend: (days?: number) =>
    apiClient.get<ApiResponse<StudentPerformanceDay[]>>('/analytics/student/performance-trend', {
      params: { days },
    }),

  getStudentSubjectPerformance: () =>
    apiClient.get<ApiResponse<SubjectPerformance[]>>('/analytics/student/subject-performance'),

  getStudentAchievements: () =>
    apiClient.get<ApiResponse<StudentAchievement[]>>('/analytics/student/achievements'),

  getStudentNotifications: (unreadOnly?: boolean) =>
    apiClient.get<ApiResponse<StudentNotification[]>>('/analytics/student/notifications', {
      params: { unread_only: unreadOnly },
    }),

  // =========================================================================
  // Admin Dashboard Endpoints
  // =========================================================================
  getAdminDashboard: (tenantId?: string) =>
    apiClient.get<ApiResponse<AdminDashboard>>('/analytics/dashboard/admin', {
      params: tenantId ? { tenant_id: tenantId } : undefined,
    }),

  getAdminUserStats: () =>
    apiClient.get<ApiResponse<AdminUserStats>>('/analytics/admin/user-stats'),

  getAdminExamStats: () =>
    apiClient.get<ApiResponse<AdminExamStats>>('/analytics/admin/exam-stats'),

  getAdminRecentActivity: (limit?: number) =>
    apiClient.get<ApiResponse<AdminActivity[]>>('/analytics/admin/recent-activity', {
      params: { limit },
    }),

  // =========================================================================
  // Teacher Dashboard Endpoints
  // =========================================================================
  getTeacherDashboard: () =>
    apiClient.get<ApiResponse<TeacherDashboard>>('/analytics/dashboard/teacher'),

  getTeacherExams: () =>
    apiClient.get<ApiResponse<TeacherExamSummary[]>>('/analytics/teacher/exams'),

  getTeacherTodaySchedule: () =>
    apiClient.get<ApiResponse<ScheduledExam[]>>('/analytics/teacher/today-schedule'),

  getTeacherPendingGrading: () =>
    apiClient.get<ApiResponse<PendingGradingItem[]>>('/analytics/teacher/pending-grading'),

  getTeacherRecentResults: (limit?: number) =>
    apiClient.get<ApiResponse<ExamResultSummary[]>>('/analytics/teacher/recent-results', {
      params: { limit },
    }),

  getTeacherQuestionBankStats: () =>
    apiClient.get<ApiResponse<QuestionBankSummary[]>>('/analytics/teacher/question-bank-stats'),

  getTeacherStudentAlerts: () =>
    apiClient.get<ApiResponse<StudentAlert[]>>('/analytics/teacher/student-alerts'),
};

// =========================================================================
// Phase 11: AI Analysis API
// =========================================================================

// AI Analysis Types
export type AIModelType = 'face_detection' | 'gaze_tracking' | 'object_detection' | 'deepfake_detection' | 'audio_analysis' | 'behavior_analysis';
export type AIModelStatus = 'not_loaded' | 'loading' | 'ready' | 'error' | 'warming';
export type AIRiskLevel = 'low' | 'medium' | 'high' | 'critical';

export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Landmark {
  name: string;
  x: number;
  y: number;
  confidence: number;
}

export interface DetectedObject {
  label: string;
  confidence: number;
  bounding_box: BoundingBox;
  is_prohibited: boolean;
}

export interface GazeDirection {
  yaw: number;
  pitch: number;
  roll: number;
  is_on_screen: boolean;
  attention: number;
}

export interface AIDetectionRequest {
  session_id: string;
  attempt_id: string;
  model_type: AIModelType;
  detected: boolean;
  confidence: number;
  label?: string;
  bounding_box?: BoundingBox;
  landmarks?: Landmark[];
  metadata?: Record<string, unknown>;
  frame_number?: number;
  processing_ms: number;
}

export interface AIDetectionResponse {
  id: string;
  session_id: string;
  attempt_id: string;
  model_type: AIModelType;
  detected: boolean;
  confidence: number;
  label?: string;
  bounding_box?: BoundingBox;
  frame_number?: number;
  processing_ms: number;
  timestamp: string;
}

export interface AIBatchDetectionRequest {
  session_id: string;
  attempt_id: string;
  batch_id?: string;
  detections: Omit<AIDetectionRequest, 'session_id' | 'attempt_id'>[];
}

export interface AIModelConfig {
  id: string;
  tenant_id: string;
  model_type: AIModelType;
  enabled: boolean;
  min_confidence: number;
  processing_interval_ms: number;
  max_concurrent_frames: number;
  model_version: string;
  settings?: Record<string, unknown>;
}

export interface AISessionState {
  id: string;
  session_id: string;
  attempt_id: string;
  model_states: Record<AIModelType, AIModelStatus>;
  face_detected: boolean;
  face_count: number;
  gaze_direction?: GazeDirection;
  detected_objects: DetectedObject[];
  risk_indicators: string[];
  overall_confidence: number;
  last_updated: string;
}

export interface AIRiskFactor {
  model_type: AIModelType;
  factor: string;
  description: string;
  score: number;
  weight: number;
}

export interface AICompositeScore {
  id: string;
  session_id: string;
  attempt_id: string;
  face_score: number;
  gaze_score: number;
  object_score: number;
  behavior_score: number;
  composite_score: number;
  risk_level: AIRiskLevel;
  factors: AIRiskFactor[];
  calculated_at: string;
}

export interface UpdateSessionStateRequest {
  model_type?: AIModelType;
  model_status?: AIModelStatus;
  face_detected?: boolean;
  face_count?: number;
  yaw?: number;
  pitch?: number;
  roll?: number;
  is_on_screen?: boolean;
  attention?: number;
  detected_objects?: DetectedObject[];
  risk_indicator?: string;
}

// AI Analysis API
export const aiAnalysisApi = {
  // =========================================================================
  // Detection Results
  // =========================================================================

  // Submit single detection
  submitDetection: (data: AIDetectionRequest) =>
    apiClient.post<ApiResponse<AIDetectionResponse>>('/ai/detections', data),

  // Submit batch detections
  submitBatchDetections: (data: AIBatchDetectionRequest) =>
    apiClient.post<ApiResponse<AIDetectionResponse[]>>('/ai/detections/batch', data),

  // Get detections by attempt
  getDetectionsByAttempt: (attemptId: string, params?: { limit?: number; offset?: number }) =>
    apiClient.get<ApiResponse<AIDetectionResponse[]>>(`/ai/attempts/${attemptId}/detections`, { params }),

  // Get detections by model type
  getDetectionsByModelType: (attemptId: string, modelType: AIModelType, params?: { limit?: number; offset?: number }) =>
    apiClient.get<ApiResponse<AIDetectionResponse[]>>(`/ai/attempts/${attemptId}/detections/${modelType}`, { params }),

  // Get recent detections for real-time monitoring
  getRecentDetections: (attemptId: string, params?: { since?: string; limit?: number }) =>
    apiClient.get<ApiResponse<AIDetectionResponse[]>>(`/ai/attempts/${attemptId}/detections/recent`, { params }),

  // =========================================================================
  // Model Configuration
  // =========================================================================

  // Get all model configs
  getModelConfigs: () =>
    apiClient.get<ApiResponse<AIModelConfig[]>>('/ai/models/configs'),

  // Get specific model config
  getModelConfig: (configId: string) =>
    apiClient.get<ApiResponse<AIModelConfig>>(`/ai/models/configs/${configId}`),

  // Create model config
  createModelConfig: (modelType: AIModelType) =>
    apiClient.post<ApiResponse<AIModelConfig>>('/ai/models/configs', { model_type: modelType }),

  // Update model config
  updateModelConfig: (configId: string, data: Partial<AIModelConfig>) =>
    apiClient.put<ApiResponse<AIModelConfig>>(`/ai/models/configs/${configId}`, data),

  // Enable model
  enableModel: (modelType: AIModelType) =>
    apiClient.post(`/ai/models/${modelType}/enable`),

  // Disable model
  disableModel: (modelType: AIModelType) =>
    apiClient.post(`/ai/models/${modelType}/disable`),

  // =========================================================================
  // Session State
  // =========================================================================

  // Initialize session state
  initializeSessionState: (sessionId: string, attemptId: string) =>
    apiClient.post<ApiResponse<AISessionState>>('/ai/sessions/state', {
      session_id: sessionId,
      attempt_id: attemptId,
    }),

  // Get session state
  getSessionState: (sessionId: string) =>
    apiClient.get<ApiResponse<AISessionState>>(`/ai/sessions/${sessionId}/state`),

  // Update session state
  updateSessionState: (sessionId: string, data: UpdateSessionStateRequest) =>
    apiClient.put<ApiResponse<void>>(`/ai/sessions/${sessionId}/state`, data),

  // Get active session states for exam
  getActiveSessionStates: (examId: string) =>
    apiClient.get<ApiResponse<AISessionState[]>>(`/ai/exams/${examId}/active-states`),

  // Get states with risk indicators
  getStatesWithRiskIndicators: (examId: string) =>
    apiClient.get<ApiResponse<AISessionState[]>>(`/ai/exams/${examId}/risk-states`),

  // =========================================================================
  // Composite Scores
  // =========================================================================

  // Calculate composite score
  calculateCompositeScore: (sessionId: string, attemptId: string) =>
    apiClient.post<ApiResponse<AICompositeScore>>(`/ai/attempts/${attemptId}/composite-score`, {
      session_id: sessionId,
      attempt_id: attemptId,
    }),

  // Get composite score
  getCompositeScore: (attemptId: string) =>
    apiClient.get<ApiResponse<AICompositeScore>>(`/ai/attempts/${attemptId}/composite-score`),

  // Get score history
  getScoreHistory: (attemptId: string, params?: { limit?: number }) =>
    apiClient.get<ApiResponse<AICompositeScore[]>>(`/ai/attempts/${attemptId}/score-history`, { params }),

  // Get high-risk sessions for exam
  getHighRiskSessions: (examId: string, params?: { min_score?: number; limit?: number }) =>
    apiClient.get<ApiResponse<AICompositeScore[]>>(`/ai/exams/${examId}/high-risk`, { params }),

  // Get risk distribution
  getRiskDistribution: (examId: string) =>
    apiClient.get<ApiResponse<Record<AIRiskLevel, number>>>(`/ai/exams/${examId}/risk-distribution`),
};

// =========================================================================
// Audit Logs API
// =========================================================================
export interface AuditLogEntry {
  id: string;
  timestamp: string;
  user_id: string;
  user_name: string;
  user_email: string;
  action: string;
  resource_type: string;
  resource_id: string;
  description: string;
  ip_address: string;
  user_agent: string;
  level: 'info' | 'warning' | 'error';
  metadata: Record<string, unknown>;
}

export const auditLogsApi = {
  getLogs: (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    user_id?: string;
    action?: string;
    resource_type?: string;
    level?: string;
    date_from?: string;
    date_to?: string;
  }) => apiClient.get<{ data: AuditLogEntry[]; meta: { total_pages: number; total: number } }>('/audit-logs', { params }),

  exportLogs: (params?: {
    search?: string;
    user_id?: string;
    action?: string;
    resource_type?: string;
    level?: string;
    date_from?: string;
    date_to?: string;
  }) => apiClient.get('/audit-logs/export', { params, responseType: 'blob' }),
};

// =========================================================================
// Backup API
// =========================================================================
export interface BackupEntry {
  id: string;
  filename: string;
  size: number;
  created_at: string;
  type: 'manual' | 'automatic' | 'scheduled';
  status: 'completed' | 'in_progress' | 'failed';
  description: string;
  database_size: number;
  tables_count: number;
}

export interface BackupScheduleConfig {
  enabled: boolean;
  frequency: 'daily' | 'weekly' | 'monthly';
  time: string;
  retention_days: number;
  max_backups: number;
}

export const backupApi = {
  getBackups: () =>
    apiClient.get<{ data: BackupEntry[] }>('/backups'),

  createBackup: (data: { description?: string }) =>
    apiClient.post<ApiResponse<BackupEntry>>('/backups', data),

  downloadBackup: (backupId: string) =>
    apiClient.get(`/backups/${backupId}/download`, { responseType: 'blob' }),

  restoreBackup: (backupId: string) =>
    apiClient.post<ApiResponse<unknown>>(`/backups/${backupId}/restore`),

  deleteBackup: (backupId: string) =>
    apiClient.delete(`/backups/${backupId}`),

  getSchedule: () =>
    apiClient.get<{ data: BackupScheduleConfig }>('/backups/schedule'),

  updateSchedule: (schedule: BackupScheduleConfig) =>
    apiClient.put<ApiResponse<BackupScheduleConfig>>('/backups/schedule', schedule),
};

// =========================================================================
// Notifications API (In-App Notifications)
// =========================================================================
export interface Notification {
  id: string;
  tenant_id: string;
  user_id: string;
  type: 'in_app' | 'email' | 'push';
  title: string;
  message: string;
  data?: Record<string, unknown>;
  status: 'pending' | 'sent' | 'failed' | 'read';
  category?: string;
  action_url?: string;
  icon?: string;
  is_deleted?: boolean;
  group_key?: string;
  sent_at?: string;
  read_at?: string;
  created_at: string;
}

export interface Announcement {
  id: string;
  tenant_id: string;
  title: string;
  content: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  target_type: string;
  target_ids?: string[];
  target_roles?: string[];
  start_at: string;
  end_at?: string;
  is_pinned: boolean;
  is_active: boolean;
  metadata?: Record<string, unknown>;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface NotificationPreference {
  id?: string;
  tenant_id?: string;
  user_id: string;
  email_enabled: boolean;
  push_enabled: boolean;
  in_app_enabled: boolean;
  preferences?: Record<string, boolean>;
  quiet_hours?: {
    enabled: boolean;
    start_time: string;
    end_time: string;
    timezone: string;
  };
}

export const notificationsApi = {
  // Get user notifications
  list: (params?: { limit?: number; offset?: number; unread_only?: boolean }) =>
    apiClient.get<{ notifications: Notification[]; unread_count: number; total: number }>('/notifications', { params }),

  // Get unread count
  getUnreadCount: () =>
    apiClient.get<{ count: number }>('/notifications/unread-count'),

  // Mark notification as read
  markAsRead: (id: string) =>
    apiClient.post<{ message: string }>(`/notifications/${id}/read`),

  // Mark all as read
  markAllAsRead: () =>
    apiClient.post<{ message: string }>('/notifications/read-all'),

  // Delete notification
  delete: (id: string) =>
    apiClient.delete(`/notifications/${id}`),

  // Get preferences
  getPreferences: () =>
    apiClient.get<NotificationPreference>('/notifications/preferences'),

  // Update preferences
  updatePreferences: (prefs: Partial<NotificationPreference>) =>
    apiClient.put<NotificationPreference>('/notifications/preferences', prefs),

  // Legacy email notification settings (for admin settings page)
  getTemplates: () =>
    apiClient.get<{ data: unknown[] }>('/notifications/templates'),

  getTemplate: (templateId: string) =>
    apiClient.get<ApiResponse<unknown>>(`/notifications/templates/${templateId}`),

  updateTemplate: (templateId: string, data: unknown) =>
    apiClient.put<ApiResponse<unknown>>(`/notifications/templates/${templateId}`, data),

  getSettings: () =>
    apiClient.get<{ data: unknown }>('/notifications/settings'),

  updateSettings: (settings: unknown) =>
    apiClient.put<ApiResponse<unknown>>('/notifications/settings', settings),

  sendTestEmail: (templateId: string, email: string) =>
    apiClient.post<ApiResponse<unknown>>('/notifications/test-email', { template_id: templateId, email }),
};

// =========================================================================
// Announcements API
// =========================================================================
export const announcementsApi = {
  // Get announcements for current user
  list: (params?: { limit?: number; offset?: number }) =>
    apiClient.get<{ announcements: Announcement[]; unread_count: number; total: number }>('/announcements', { params }),

  // Get announcement by ID
  get: (id: string) =>
    apiClient.get<Announcement>(`/announcements/${id}`),

  // Create announcement (teachers/admins)
  create: (data: {
    title: string;
    content: string;
    priority?: string;
    target_type?: string;
    target_ids?: string[];
    target_roles?: string[];
    start_at?: string;
    end_at?: string;
    is_pinned?: boolean;
  }) =>
    apiClient.post<Announcement>('/announcements', data),

  // Update announcement
  update: (id: string, data: Partial<Announcement>) =>
    apiClient.put<Announcement>(`/announcements/${id}`, data),

  // Delete announcement
  delete: (id: string) =>
    apiClient.delete(`/announcements/${id}`),

  // Mark as read
  markAsRead: (id: string) =>
    apiClient.post<{ message: string }>(`/announcements/${id}/read`),
};

// =========================================================================
// Certificates API (via nexus-gateway → nexus-certchain)
// =========================================================================

// Certchain certificate (full detail)
export interface Certificate {
  id: string;
  tenant_id: string;
  template_id: string;
  code: string;
  status: 'pending' | 'issued' | 'verified' | 'revoked' | 'expired';
  recipient_id?: string;
  recipient_name: string;
  recipient_email?: string;
  title: string;
  description?: string;
  field_values: Record<string, any>;
  metadata?: Record<string, any>;
  source_type: string;
  source_id?: string;
  pdf_url?: string;
  blockchain_status: string;
  transaction_hash?: string;
  issued_at: string;
  expires_at?: string;
  revoked_at?: string;
  revoked_by?: string;
  revocation_reason?: string;
  view_count: number;
  created_at: string;
  updated_at: string;
  issued_by?: string;
}

// Certchain simplified "my certificates" response
export interface MyCertificate {
  id: string;
  course_id: string;
  course_title: string;
  user_id: string;
  user_name: string;
  issued_at: string;
  certificate_url: string;
  certificate_number: string;
  grade?: string;
  completion_date?: string;
  blockchain_status?: string;
  transaction_hash?: string;
}

// Certchain template
export interface CertificateTemplate {
  id: string;
  name: string;
  slug: string;
  description?: string;
  type: string;
  type_display: string;
  status: string;
  status_display: string;
  version: number;
  is_default: boolean;
  is_system_owned: boolean;
  preview_url?: string;
  usage_count: number;
  design?: Record<string, any>;
  fields?: CertificateTemplateField[];
  created_at: string;
  updated_at: string;
}

export interface CertificateTemplateField {
  name: string;
  label: string;
  type: string;
  required: boolean;
  default_value?: string;
  placeholder?: string;
  order: number;
}

import { gatewayClient } from './gatewayClient';

export const certificatesApi = {
  // Templates (certchain)
  getTemplates: (params?: { type?: string; status?: string; page?: number; per_page?: number }) =>
    gatewayClient.get<{ data: CertificateTemplate[]; meta?: any }>('/api/v1/templates', { params: { ...params, status: params?.status || 'active' } }),

  getTemplate: (id: string) =>
    gatewayClient.get<{ data: CertificateTemplate }>(`/api/v1/templates/${id}`),

  // My certificates (student view)
  getMyCertificates: (params?: { page?: number; per_page?: number }) =>
    gatewayClient.get<{ data: MyCertificate[]; meta?: any }>('/api/v1/certificates/my', { params }),

  // Certificate CRUD
  get: (id: string) =>
    gatewayClient.get<{ data: Certificate }>(`/api/v1/certificates/${id}`),

  list: (params?: { template_id?: string; status?: string; search?: string; source_type?: string; source_id?: string; page?: number; per_page?: number }) =>
    gatewayClient.get<{ data: Certificate[]; meta?: any }>('/api/v1/certificates', { params }),

  create: (data: { template_id: string; recipient_name: string; recipient_email?: string; title: string; field_values?: Record<string, any>; source_type?: string; source_id?: string }) =>
    gatewayClient.post<{ data: Certificate }>('/api/v1/certificates', data),

  bulkCreate: (data: { template_id: string; recipients: Array<{ name: string; email?: string; field_values?: Record<string, any> }>; title?: string; source_type?: string; source_id?: string }) =>
    gatewayClient.post<{ data: { created: number; failed: number; certificates: Certificate[] } }>('/api/v1/certificates/bulk', data),

  // Download / verify / revoke
  download: (id: string) =>
    gatewayClient.get(`/api/v1/certificates/${id}/download`, { responseType: 'blob' }),

  verify: (code: string) =>
    gatewayClient.get<{ data: { valid: boolean; certificate?: Certificate } }>(`/api/v1/verify/${code}`),

  revoke: (id: string, reason: string) =>
    gatewayClient.post<{ data: { message: string } }>(`/api/v1/certificates/${id}/revoke`, { reason }),

  sendEmail: (id: string) =>
    gatewayClient.post<{ data: { message: string } }>(`/api/v1/certificates/${id}/send-email`),

  // Stats
  getStats: () =>
    gatewayClient.get<{ data: any }>('/api/v1/stats/certificates'),

  getDashboardStats: () =>
    gatewayClient.get<{ data: any }>('/api/v1/stats/dashboard'),

  // Template CRUD (admin)
  createTemplate: (data: { name: string; description?: string; type: string; status?: string; fields?: CertificateTemplateField[] }) =>
    gatewayClient.post<{ data: CertificateTemplate }>('/api/v1/templates', data),

  updateTemplate: (id: string, data: { name?: string; description?: string; type?: string; status?: string; fields?: CertificateTemplateField[] }) =>
    gatewayClient.put<{ data: CertificateTemplate }>(`/api/v1/templates/${id}`, data),

  deleteTemplate: (id: string) =>
    gatewayClient.delete(`/api/v1/templates/${id}`),

  setDefaultTemplate: (id: string) =>
    gatewayClient.post(`/api/v1/templates/${id}/set-default`),
};

// =========================================================================
// Gradebook API
// =========================================================================
export const gradebookApi = {
  getExamGrades: (examId: string, params?: { page?: number; per_page?: number }) =>
    apiClient.get<{ data: unknown[]; meta: { total: number; total_pages: number } }>(`/exams/${examId}/grades`, { params }),

  updateGrade: (attemptId: string, data: { score: number; feedback?: string }) =>
    apiClient.put<ApiResponse<unknown>>(`/attempts/${attemptId}/grade`, data),

  bulkUpdateGrades: (data: { grades: Array<{ attempt_id: string; score: number; feedback?: string }> }) =>
    apiClient.post<ApiResponse<{ updated: number }>>('/grades/bulk-update', data),

  exportGrades: (examId: string) =>
    apiClient.get(`/exams/${examId}/grades/export`, { responseType: 'blob' }),

  getGradeStats: (examId: string) =>
    apiClient.get<ApiResponse<unknown>>(`/exams/${examId}/grades/stats`),

  recalculateGrades: (examId: string) =>
    apiClient.post<ApiResponse<{ recalculated: number }>>(`/exams/${examId}/grades/recalculate`),
};

// =========================================================================
// Reports API
// =========================================================================
export const reportsApi = {
  getAvailableReports: () =>
    apiClient.get<{ data: unknown[] }>('/reports'),

  generateReport: (reportType: string, params?: unknown) =>
    apiClient.post<Blob>(`/reports/${reportType}/generate`, params, { responseType: 'blob' }),

  scheduleReport: (reportType: string, schedule: unknown) =>
    apiClient.post<ApiResponse<unknown>>(`/reports/${reportType}/schedule`, schedule),

  getScheduledReports: () =>
    apiClient.get<{ data: unknown[] }>('/reports/scheduled'),

  deleteScheduledReport: (scheduleId: string) =>
    apiClient.delete(`/reports/scheduled/${scheduleId}`),
};

// =========================================================================
// System Settings API
// =========================================================================
export const systemSettingsApi = {
  getAll: () =>
    apiClient.get<{ data: Record<string, unknown> }>('/settings'),

  get: (key: string) =>
    apiClient.get<ApiResponse<unknown>>(`/settings/${key}`),

  update: (key: string, value: unknown) =>
    apiClient.put<ApiResponse<unknown>>(`/settings/${key}`, { value }),

  bulkUpdate: (settings: Record<string, unknown>) =>
    apiClient.put<ApiResponse<unknown>>('/settings', settings),
};

// =========================================================================
// System Health API
// =========================================================================
export const systemHealthApi = {
  getStatus: () =>
    apiClient.get<ApiResponse<{
      status: string;
      uptime: number;
      version: string;
      database: { status: string; latency_ms: number };
      cache: { status: string; hit_rate: number };
      storage: { status: string; used_bytes: number; total_bytes: number };
    }>>('/health'),

  getMetrics: () =>
    apiClient.get<ApiResponse<unknown>>('/health/metrics'),

  getLogs: (params?: { level?: string; limit?: number }) =>
    apiClient.get<{ data: unknown[] }>('/health/logs', { params }),
};

// =========================================================================
// Question Import/Export API
// =========================================================================
export const questionImportExportApi = {
  importQuestions: (formData: FormData) =>
    apiClient.post<ApiResponse<{ imported: number; failed: number; errors: string[] }>>('/questions/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  exportQuestions: (params?: { question_bank_id?: string; format?: 'json' | 'csv' }) =>
    apiClient.get('/questions/export', { params, responseType: 'blob' }),

  getImportTemplate: (format: 'json' | 'csv') =>
    apiClient.get(`/questions/import-template?format=${format}`, { responseType: 'blob' }),
};

// =========================================================================
// Live Exam Monitor API
// =========================================================================
export const liveExamMonitorApi = {
  getActiveExams: () =>
    apiClient.get<{ data: unknown[] }>('/exams/live'),

  getExamSession: (examId: string) =>
    apiClient.get<ApiResponse<unknown>>(`/exams/${examId}/live-session`),

  getActiveStudents: (examId: string) =>
    apiClient.get<{ data: unknown[] }>(`/exams/${examId}/active-students`),

  sendBroadcast: (examId: string, message: string) =>
    apiClient.post<ApiResponse<unknown>>(`/exams/${examId}/broadcast`, { message }),

  extendTime: (examId: string, data: { minutes: number; student_ids?: string[] }) =>
    apiClient.post<ApiResponse<unknown>>(`/exams/${examId}/extend-time`, data),

  pauseExam: (examId: string, studentIds?: string[]) =>
    apiClient.post<ApiResponse<unknown>>(`/exams/${examId}/pause`, { student_ids: studentIds }),

  resumeExam: (examId: string, studentIds?: string[]) =>
    apiClient.post<ApiResponse<unknown>>(`/exams/${examId}/resume`, { student_ids: studentIds }),
};

// =========================================================================
// System Check Wizard API
// =========================================================================
export const systemCheckApi = {
  runCheck: (checkType: string) =>
    apiClient.post<ApiResponse<{ passed: boolean; message: string; details?: unknown }>>(`/system/check/${checkType}`),

  runAllChecks: () =>
    apiClient.post<ApiResponse<Array<{ type: string; passed: boolean; message: string }>>>('/system/check/all'),
};

// =========================================================================
// Plagiarism Detection API
// =========================================================================
export const plagiarismApi = {
  analyzeAnswer: (data: { answer: { answer_id: string; question_id: string; student_id: string; attempt_id: string; content: string; content_type?: string }; comparisons?: Array<{ answer_id: string; question_id: string; student_id: string; attempt_id: string; content: string; content_type?: string }>; config?: any }) =>
    apiClient.post<ApiResponse<any>>('/plagiarism/analyze', data),

  analyzeQuestion: (data: { question_id: string; exam_id: string; answers: Array<{ answer_id: string; question_id: string; student_id: string; attempt_id: string; content: string; content_type?: string }>; config?: any }) =>
    apiClient.post<ApiResponse<any>>('/plagiarism/analyze-question', data),

  compareAnswers: (data: { answer1: { answer_id: string; content: string }; answer2: { answer_id: string; content: string } }) =>
    apiClient.post<ApiResponse<any>>('/plagiarism/compare', data),

  getResult: (id: string) =>
    apiClient.get<ApiResponse<any>>(`/plagiarism/results/${id}`),

  getExamResults: (examId: string, params?: { limit?: number; offset?: number }) =>
    apiClient.get<ApiResponse<any>>(`/plagiarism/exams/${examId}/results`, { params }),

  getStudentResults: (studentId: string, params?: { limit?: number; offset?: number }) =>
    apiClient.get<ApiResponse<any>>(`/plagiarism/students/${studentId}/results`, { params }),

  getFlaggedResults: (params?: { severity?: string; limit?: number; offset?: number }) =>
    apiClient.get<ApiResponse<any>>('/plagiarism/flagged', { params }),

  getStats: () =>
    apiClient.get<ApiResponse<{ total_results: number; flagged_count: number; average_similarity: number; max_similarity: number; severity_distribution: Record<string, number>; cluster_count: number; recent_flags: number }>>('/plagiarism/stats'),

  getExamStats: (examId: string) =>
    apiClient.get<ApiResponse<{ total_results: number; flagged_count: number; average_similarity: number; max_similarity: number; severity_distribution: Record<string, number>; cluster_count: number; recent_flags: number }>>(`/plagiarism/exams/${examId}/stats`),

  getClusters: (examId: string) =>
    apiClient.get<ApiResponse<any>>(`/plagiarism/exams/${examId}/clusters`),

  analyzeExam: (examId: string) =>
    apiClient.post<ApiResponse<any>>(`/plagiarism/analyze-exam/${examId}`),

  getDefaultConfig: () =>
    apiClient.get<ApiResponse<any>>('/plagiarism/config/default'),

  updateResultReview: (id: string, data: { status: string; reviewer_notes: string }) =>
    apiClient.patch<ApiResponse<any>>(`/plagiarism/results/${id}/review`, data),

  addReviewNote: (resultId: string, data: { note: string; status_change?: string }) =>
    apiClient.post<ApiResponse<any>>(`/plagiarism/results/${resultId}/notes`, data),

  getReviewNotes: (resultId: string) =>
    apiClient.get<ApiResponse<any>>(`/plagiarism/results/${resultId}/notes`),

  applyPenalty: (resultId: string, data: { attempt_id: string; penalty_percent: number; reason: string }) =>
    apiClient.post<ApiResponse<any>>(`/plagiarism/results/${resultId}/penalty`, data),

  exportExamResults: (examId: string) =>
    apiClient.get<Blob>(`/plagiarism/exams/${examId}/export`, { responseType: 'blob' }),

  getStudentSummary: (studentId: string) =>
    apiClient.get<ApiResponse<any>>(`/plagiarism/students/${studentId}/summary`),

  bulkReview: (data: { result_ids: string[]; status: string; note: string }) =>
    apiClient.patch<ApiResponse<any>>('/plagiarism/results/bulk-review', data),
};

// =========================================================================
// Partner / Affiliate API
// =========================================================================
export const partnersApi = {
  create: (data: { partner_name: string; company_name?: string; contact_email: string; contact_phone?: string; partner_code: string; commission_rate: number }) =>
    apiClient.post<ApiResponse<any>>('/partners', data),

  list: (params?: { limit?: number; offset?: number }) =>
    apiClient.get<ApiResponse<any>>('/partners', { params }),

  getPerformance: () =>
    apiClient.get<ApiResponse<any>>('/partners/performance'),

  getDashboard: (partnerId: string) =>
    apiClient.get<ApiResponse<any>>(`/partners/${partnerId}/dashboard`),

  generateLink: (partnerId: string, params?: { exam_id?: string }) =>
    apiClient.get<ApiResponse<any>>(`/partners/${partnerId}/generate-link`, { params }),
};

// =========================================================================
// Exam Templates API
// =========================================================================
export const templatesApi = {
  list: (params?: any) => apiClient.get('/exam-templates', { params }),
  getById: (id: string) => apiClient.get(`/exam-templates/${id}`),
  create: (data: any) => apiClient.post('/exam-templates', data),
  update: (id: string, data: any) => apiClient.put(`/exam-templates/${id}`, data),
  delete: (id: string) => apiClient.delete(`/exam-templates/${id}`),
  getStats: () => apiClient.get('/exam-templates/stats'),
};

